import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { Observable, map } from 'rxjs';

import { AccountService } from '../../services/account.service';
import { BusyService } from '../../services/busy.service';
import { RequiredNotificationsService } from '../notifications/notifications.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatMenuModule],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly requiredNotificationsService = inject(RequiredNotificationsService);
  private readonly employeeColorCount = 8;

  menuOpen = false;
  submenuOpen: string | null = null;

  accountService = inject(AccountService);
  busyService = inject(BusyService);

  hoveredItem: HTMLElement | null = null;
  hoverTimeout: ReturnType<typeof setTimeout> | null = null;

  settingsOpen = false;
  mobileTimesheetsOpen = false;
  mobileSettingsOpen = false;
  mobileUserMenuOpen = false;

  readonly notificationCount$: Observable<number> =
    this.requiredNotificationsService.notifications$.pipe(
      map((notifications) =>
        this.requiredNotificationsService.getTotalNotificationCount(notifications),
      ),
    );

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;

    const clickedInsideNavbar = this.elementRef.nativeElement.contains(target);

    if (!clickedInsideNavbar) {
      this.closeAllMenus();
    }
  }

  toggleSettingsPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.settingsOpen = !this.settingsOpen;
    this.submenuOpen = null;
  }

  closePanels(): void {
    this.settingsOpen = false;
    this.submenuOpen = null;
  }

  onMouseEnter(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    this.hoveredItem = target;
    target.classList.add('hovered');
  }

  onMouseLeave(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;

    this.hoverTimeout = setTimeout(() => {
      target.classList.remove('hovered');
      this.hoveredItem = null;
    }, 250);
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;

    if (!this.menuOpen) {
      this.mobileTimesheetsOpen = false;
      this.mobileSettingsOpen = false;
      this.mobileUserMenuOpen = false;
    }
  }

  toggleSubmenu(name: string, event: MouseEvent): void {
    event.stopPropagation();

    this.settingsOpen = false;
    this.submenuOpen = this.submenuOpen === name ? null : name;
  }

  toggleMobileTimesheets(event: Event): void {
    event.stopPropagation();

    this.mobileTimesheetsOpen = !this.mobileTimesheetsOpen;

    if (this.mobileTimesheetsOpen) {
      this.mobileSettingsOpen = false;
      this.mobileUserMenuOpen = false;
    }
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();

    this.mobileUserMenuOpen = !this.mobileUserMenuOpen;

    if (this.mobileUserMenuOpen) {
      this.mobileTimesheetsOpen = false;
      this.mobileSettingsOpen = false;
    }
  }

  toggleMobileSettings(event: Event): void {
    event.stopPropagation();

    this.mobileSettingsOpen = !this.mobileSettingsOpen;

    if (this.mobileSettingsOpen) {
      this.mobileTimesheetsOpen = false;
      this.mobileUserMenuOpen = false;
    }
  }

  closeAllMenus(): void {
    this.menuOpen = false;
    this.submenuOpen = null;
    this.settingsOpen = false;
    this.mobileTimesheetsOpen = false;
    this.mobileSettingsOpen = false;
    this.mobileUserMenuOpen = false;
  }

  get userInitials(): string {
    const user = this.accountService.user;
    if (!user) return '';

    const first = user.firstName?.[0] ?? '';
    const last = user.lastName?.[0] ?? '';

    return (first + last).toUpperCase();
  }

  get isAdmin(): boolean {
    return (this.accountService.user?.roles ?? []).some(
      (role) => role.name.toLowerCase() === 'admin',
    );
  }

  get userFullName(): string {
    const user = this.accountService.user;
    if (!user) return '';

    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }

  get userEmail(): string {
    const user = this.accountService.user as any;
    return user?.email ?? user?.emailAddress ?? user?.username ?? '';
  }

  get userEmployeeColorClass(): string {
    const user = this.accountService.user as any;

    return this.getEmployeeColorClassById(
      user?.id ?? user?.employeeId ?? user?.personId ?? user?.userId,
      user?.employeeNumber ?? user?.employeeNo ?? user?.personNumber ?? user?.username,
    );
  }

  getEmployeeColorClassById(
    id: number | string | null | undefined,
    employeeNumber?: string | number | null,
  ): string {
    const rawId = Number(id);
    const rawEmployeeNumber = Number(employeeNumber);
    const raw = Number.isFinite(rawId) && rawId > 0
      ? rawId
      : Number.isFinite(rawEmployeeNumber) && rawEmployeeNumber > 0
        ? rawEmployeeNumber
        : 0;

    const index = Math.abs(raw) % this.employeeColorCount;

    return `employee-color-${index}`;
  }
}
